/**
 * SubscribeFromBlock — Phase 2b (Sachin spec 2026-02-23).
 *
 * Lets the coach subscribe an athlete to a pricing block, either as a
 * real recurring Stripe Subscription (auto-bill) or a single upfront
 * lump-sum charge that covers the full commitment.
 *
 * Routes through `POST /api/coach/clients/{user_id}/blocks/{block_id}/subscribe`.
 * No Stripe code on the FE — backend handles Subscription + PaymentIntent.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Repeat, CreditCard, AlertTriangle, Calendar } from "lucide-react";
import RiskyChargeConfirmModal from "@/components/coach/RiskyChargeConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

const TAX_RATE_PREVIEW = 0.103;  // display only; backend computes real rate

const intervalShort = (i) => ({ daily: "day", weekly: "wk", monthly: "mo" }[i] || i);
const intervalLong = (i) => ({ daily: "day", weekly: "week", monthly: "month" }[i] || i);


export default function SubscribeFromBlock({ userId, athletes = [], onSubscribed }) {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [selectedAthleteId, setSelectedAthleteId] = useState(athletes[0]?.athlete_id || "");
    const [paymentMode, setPaymentMode] = useState("recurring");
    // Coach-applied discount (Sachin 2026-02-28). Mode picks which input
    // is live; "none" disables the section. Duration only matters for
    // recurring (upfront is forced to "once" server-side regardless).
    const [discountMode, setDiscountMode] = useState("none"); // "none" | "percent" | "fixed"
    const [discountInput, setDiscountInput] = useState("");
    const [discountDuration, setDiscountDuration] = useState("once"); // "once" | "repeating" | "forever"
    const [discountPeriods, setDiscountPeriods] = useState("");
    const [discountReason, setDiscountReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!selectedAthleteId && athletes.length > 0) {
            setSelectedAthleteId(athletes[0].athlete_id);
        }
    }, [athletes, selectedAthleteId]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/pricing-blocks`, { withCredentials: true });
                if (!alive) return;
                // Only blocks WITH a subscription configured can be subscribed-from.
                // Coach-side sees ALL sub blocks (3-week, 6-week, 12-week) so
                // shorter commitments can still be sold; the public
                // /continue page + pricing email are filtered separately.
                // Sachin 2026-02-28 clarified: "Can you make sure I can
                // still charge them but not show on the email?"
                const subblocks = (r.data?.blocks || []).filter(
                    (b) => b.active && b.subscription?.enabled,
                );
                setBlocks(subblocks);
                if (subblocks.length > 0) setSelectedBlockId(subblocks[0].block_id);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.detail || "Couldn't load pricing blocks");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const block = useMemo(
        () => blocks.find((b) => b.block_id === selectedBlockId) || null,
        [blocks, selectedBlockId],
    );

    // Lock paymentMode to whichever option the selected block allows.
    useEffect(() => {
        if (!block?.subscription) return;
        const { allow_recurring, allow_upfront } = block.subscription;
        if (paymentMode === "recurring" && !allow_recurring && allow_upfront) {
            setPaymentMode("upfront");
        } else if (paymentMode === "upfront" && !allow_upfront && allow_recurring) {
            setPaymentMode("recurring");
        }
    }, [block, paymentMode]);

    const sub = block?.subscription;
    const recurringTotal = sub ? sub.price_cents : 0;
    const upfrontSubtotal = sub ? sub.price_cents * sub.commitment_count : 0;
    const upfrontTaxPreview = Math.round(upfrontSubtotal * TAX_RATE_PREVIEW);
    const upfrontTotalPreview = upfrontSubtotal + upfrontTaxPreview;

    // Sachin 2026-03-06: replacing native `window.confirm` with a real
    // modal (RiskyChargeConfirmModal) that hard-gates $0 / forever
    // discounts with a type-to-confirm token. `pending` holds every
    // computed line + discount summary needed to render the modal AND
    // fire the actual API call when the coach confirms.
    const [pending, setPending] = useState(null);

    const subscribe = () => {
        if (!block || !sub || busy) return;
        if (!selectedAthleteId) {
            toast.error("Pick an athlete first");
            return;
        }
        const athleteName = athletes.find((a) => a.athlete_id === selectedAthleteId)?.first_name
            || athletes.find((a) => a.athlete_id === selectedAthleteId)?.name
            || "this athlete";

        // Ad-hoc discount preview.
        const adHocDiscountAmount = (() => {
            const v = parseFloat(discountInput);
            if (!v || v <= 0 || discountMode === "none") return 0;
            if (discountMode === "percent") {
                return Math.round(sub.price_cents * (v / 100));
            }
            return Math.min(Math.round(v * 100), sub.price_cents);
        })();
        const adHocDiscountKind = adHocDiscountAmount > 0 ? (discountMode === "percent" ? "percent" : "fixed") : null;
        const adHocDiscountValue = adHocDiscountKind ? parseFloat(discountInput) : 0;

        // If the coach didn't enter an ad-hoc discount, check whether
        // the block itself carries a silent auto-apply coupon config.
        // Backend applies it either way — surfacing it here so the
        // modal can flag `source = "block_coupon"` and the coach sees
        // it BEFORE clicking.
        const blockCoupon = adHocDiscountAmount === 0 && block?.coupon?.enabled ? block.coupon : null;
        const blockDiscountAmount = (() => {
            if (!blockCoupon) return 0;
            if (blockCoupon.mode === "percent") {
                return Math.round(sub.price_cents * ((Number(blockCoupon.value) || 0) / 100));
            }
            return Math.min(Number(blockCoupon.value_cents) || 0, sub.price_cents);
        })();

        const usingBlockCoupon = blockDiscountAmount > 0;
        const discountAmount = adHocDiscountAmount + blockDiscountAmount;  // one is always 0
        const hasDiscount = discountAmount > 0;
        const effectivePeriod = sub.price_cents - discountAmount;

        // Effective duration for the risk classifier + label.
        const effectiveDurationKind = (() => {
            if (paymentMode === "upfront") return "one_time";
            if (usingBlockCoupon) return blockCoupon.duration || "once";
            return discountDuration;
        })();
        const durationLabel = (() => {
            if (paymentMode === "upfront") return "once";
            if (effectiveDurationKind === "forever") return "forever";
            if (effectiveDurationKind === "repeating") {
                const n = usingBlockCoupon
                    ? (parseInt(blockCoupon.periods, 10) || sub.commitment_count)
                    : (parseInt(discountPeriods, 10) || sub.commitment_count);
                return `for ${Math.min(n, sub.commitment_count)} ${intervalLong(sub.interval)}${n === 1 ? "" : "s"}`;
            }
            return "first charge only";
        })();

        // Modal props.
        const isRecurring = paymentMode === "recurring";
        const modalDiscount = hasDiscount ? {
            kind: usingBlockCoupon
                ? (blockCoupon.mode === "percent" ? "percent" : "fixed")
                : adHocDiscountKind,
            value: usingBlockCoupon
                ? (blockCoupon.mode === "percent" ? Number(blockCoupon.value) : (Number(blockCoupon.value_cents) / 100))
                : adHocDiscountValue,
            durationLabel,
            durationKind: effectiveDurationKind,
            source: usingBlockCoupon ? "block_coupon" : "ad_hoc",
            reason: usingBlockCoupon
                ? (blockCoupon.reason || `Block coupon: ${block.name}`)
                : (discountReason.trim() || undefined),
        } : null;

        // Line items for the modal — recurring shows per-period; upfront
        // shows the multi-period ledger so the coach sees the total.
        const upfrontDiscount = discountAmount * sub.commitment_count;
        const upfrontAfter = upfrontSubtotal - upfrontDiscount;
        const upfrontTaxAfter = Math.round(upfrontAfter * TAX_RATE_PREVIEW);
        const lines = isRecurring
            ? [{
                label: `${block.name} · ${intervalLong(sub.interval)}ly`,
                sub: `${sub.commitment_count} ${intervalLong(sub.interval)}${sub.commitment_count === 1 ? "" : "s"} commitment`,
                amountCents: sub.price_cents,
                strike: hasDiscount,
            }].concat(hasDiscount ? [{
                label: "After discount",
                amountCents: effectivePeriod,
            }] : [])
            : [
                {
                    label: `${sub.commitment_count} × ${block.name}`,
                    sub: `${fmtMoney(sub.price_cents)}/${intervalShort(sub.interval)} × ${sub.commitment_count}`,
                    amountCents: upfrontSubtotal,
                    strike: hasDiscount,
                },
                ...(hasDiscount ? [{ label: "− Discount", amountCents: -upfrontDiscount }] : []),
                ...(hasDiscount ? [{ label: "= Subtotal after discount", amountCents: upfrontAfter }] : []),
                { label: `+ Tax (~${(TAX_RATE_PREVIEW * 100).toFixed(1)}%)`, amountCents: hasDiscount ? upfrontTaxAfter : upfrontTaxPreview },
            ];

        setPending({
            athleteName,
            hasDiscount,
            usingBlockCoupon,
            adHocDiscountKind,
            adHocDiscountValue,
            effectivePeriod,
            durationLabel,
            title: isRecurring
                ? `Start recurring · ${block.name}`
                : `Charge upfront · ${block.name}`,
            lines,
            basePriceCents: isRecurring ? sub.price_cents : upfrontSubtotal + upfrontTaxPreview,
            effectivePriceCents: isRecurring
                ? effectivePeriod
                : (hasDiscount ? upfrontAfter + upfrontTaxAfter : upfrontSubtotal + upfrontTaxPreview),
            interval: isRecurring ? sub.interval : null,
            discount: modalDiscount,
            footNote: isRecurring
                ? "First charge fires NOW on the card on file. Recurring charges auto-bill on the same schedule."
                : `Single charge NOW. Access expires after ${sub.commitment_count} ${intervalLong(sub.interval)}${sub.commitment_count === 1 ? "" : "s"}.`,
        });
    };

    // Fired when the coach clicks Confirm inside the risky-charge modal.
    const commit = async () => {
        if (!pending || !block || !sub) return;
        setBusy(true);
        const { athleteName, hasDiscount, adHocDiscountKind, effectivePeriod, durationLabel } = pending;
        try {
            const discountPayload = {};
            if (adHocDiscountKind === "percent" && parseFloat(discountInput) > 0) {
                discountPayload.discount_pct = parseFloat(discountInput);
            } else if (adHocDiscountKind === "fixed" && parseFloat(discountInput) > 0) {
                discountPayload.discount_cents = Math.round(parseFloat(discountInput) * 100);
            }
            if (Object.keys(discountPayload).length > 0) {
                discountPayload.discount_duration =
                    paymentMode === "upfront" ? "once" : discountDuration;
                if (discountPayload.discount_duration === "repeating") {
                    const n = parseInt(discountPeriods, 10);
                    if (n > 0) discountPayload.discount_periods = n;
                }
                if (discountReason.trim()) {
                    discountPayload.discount_reason = discountReason.trim();
                }
            }
            const r = await axios.post(
                `${API}/coach/clients/${userId}/blocks/${block.block_id}/subscribe`,
                { payment_mode: paymentMode, athlete_id: selectedAthleteId, ...discountPayload },
                { withCredentials: true },
            );
            if (paymentMode === "recurring") {
                const discTag = hasDiscount
                    ? ` · ${discountMode === "percent" ? `${parseFloat(discountInput)}% off` : `${fmtMoney(Math.round(parseFloat(discountInput) * 100))} off`} ${durationLabel}`
                    : "";
                toast.success("Subscription started", {
                    description: `${block.name} · ${fmtMoney(effectivePeriod)}/${intervalShort(sub.interval)} on ${athleteName}${discTag}`,
                });
            } else {
                const total = r.data?.total_cents || upfrontTotalPreview;
                const exp = r.data?.expires_at ? new Date(r.data.expires_at).toLocaleDateString() : "—";
                const discTag = hasDiscount
                    ? ` · ${discountMode === "percent" ? `${parseFloat(discountInput)}% off` : `${fmtMoney(Math.round(parseFloat(discountInput) * 100))} off`}`
                    : "";
                toast.success("Upfront charge succeeded", {
                    description: `${block.name} · ${fmtMoney(total)} · access through ${exp}${discTag}`,
                });
            }
            setPending(null);
            onSubscribed?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : undefined;
            const msg = typeof detail === "object" ? detail?.message : (detail || e?.message);
            if (code === "no_payment_method" || code === "no_customer") {
                toast.error("No card on file", { description: "Add a card via 'Setup card on file' before subscribing." });
            } else if (code === "already_subscribed_to_block") {
                toast.error("Already subscribed", { description: "This athlete already has an active subscription on this block." });
            } else {
                toast.error("Couldn't start subscription", { description: typeof msg === "string" ? msg : "Unknown error" });
            }
        } finally {
            setBusy(false);
        }
    };


    if (loading) {
        return (
            <div className="flex items-center gap-2 text-zinc-400 text-xs py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading pricing blocks…
            </div>
        );
    }
    if (error) {
        return (
            <div className="text-xs text-amber-400 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
        );
    }
    if (blocks.length === 0) {
        return (
            <div className="text-xs text-zinc-400">
                No active blocks with a subscription configured. Go to{" "}
                <span className="text-brand-red">Owner → Pricing Blocks</span> to set one up.
            </div>
        );
    }

    return (
        <div data-testid="subscribe-from-block" className="space-y-4">
            {/* Block picker */}
            <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">1 · Pick a block</div>
                <div className="flex flex-wrap gap-2">
                    {blocks.map((b) => {
                        const selected = b.block_id === selectedBlockId;
                        return (
                            <button
                                key={b.block_id}
                                type="button"
                                onClick={() => setSelectedBlockId(b.block_id)}
                                data-testid={`sfb-block-${b.block_id}`}
                                className={`text-[11px] tracking-[0.15em] uppercase px-2.5 py-1.5 border transition-colors ${
                                    selected
                                        ? "bg-brand-red border-brand-red text-white"
                                        : "border-zinc-700 text-zinc-300 hover:border-brand-red"
                                }`}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <Repeat className="w-3 h-3" />
                                    {b.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Athlete picker */}
            {athletes.length > 1 && (
                <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">2 · For which athlete?</div>
                    <div className="flex flex-wrap gap-2">
                        {athletes.map((a) => {
                            const selected = a.athlete_id === selectedAthleteId;
                            return (
                                <button
                                    key={a.athlete_id}
                                    type="button"
                                    onClick={() => setSelectedAthleteId(a.athlete_id)}
                                    data-testid={`sfb-athlete-${a.athlete_id}`}
                                    className={`text-[11px] px-2.5 py-1 border ${
                                        selected
                                            ? "bg-zinc-800 border-zinc-600 text-white"
                                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                    }`}
                                >
                                    {a.first_name || a.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Payment-mode radio */}
            {sub && (
                <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                        {athletes.length > 1 ? "3" : "2"} · Payment mode
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <button
                            type="button"
                            disabled={!sub.allow_recurring}
                            onClick={() => setPaymentMode("recurring")}
                            data-testid="sfb-mode-recurring"
                            className={`text-left px-3 py-3 border transition-colors ${
                                paymentMode === "recurring"
                                    ? "bg-emerald-700/20 border-emerald-600 text-emerald-100"
                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                            } ${!sub.allow_recurring ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                            <div className="flex items-center gap-1.5">
                                <Repeat className="w-3.5 h-3.5" />
                                <span className="text-[11px] font-semibold tracking-[0.15em] uppercase">Auto-bill</span>
                            </div>
                            <div className="text-base font-mono mt-1.5">
                                {fmtMoney(recurringTotal)}<span className="text-[10px] text-zinc-400">/{intervalShort(sub.interval)}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                                for {sub.commitment_count} {intervalLong(sub.interval)}{sub.commitment_count === 1 ? "" : "s"} · auto-charges card
                            </div>
                        </button>
                        <button
                            type="button"
                            disabled={!sub.allow_upfront}
                            onClick={() => setPaymentMode("upfront")}
                            data-testid="sfb-mode-upfront"
                            className={`text-left px-3 py-3 border transition-colors ${
                                paymentMode === "upfront"
                                    ? "bg-brand-red/15 border-brand-red text-white"
                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                            } ${!sub.allow_upfront ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                            <div className="flex items-center gap-1.5">
                                <CreditCard className="w-3.5 h-3.5" />
                                <span className="text-[11px] font-semibold tracking-[0.15em] uppercase">Pay upfront</span>
                            </div>
                            <div className="text-base font-mono mt-1.5">
                                {fmtMoney(upfrontSubtotal)} <span className="text-[10px] text-zinc-500">+ tax</span>{" "}
                                <span className="text-zinc-400">~{fmtMoney(upfrontTotalPreview)}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                                single charge · access ends in {sub.commitment_count} {intervalLong(sub.interval)}{sub.commitment_count === 1 ? "" : "s"}
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Discount section (Sachin 2026-02-28) */}
            {sub && (
                <div data-testid="sfb-discount-section">
                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                        {athletes.length > 1 ? "4" : "3"} · Discount <span className="text-zinc-600 normal-case tracking-normal text-[10px]">· optional</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 mb-2">
                        {[
                            { v: "none", label: "None" },
                            { v: "percent", label: "% off" },
                            { v: "fixed", label: "$ off" },
                        ].map((m) => (
                            <button
                                key={m.v}
                                type="button"
                                data-testid={`sfb-discount-mode-${m.v}`}
                                onClick={() => { setDiscountMode(m.v); setDiscountInput(""); }}
                                className={`text-[10px] tracking-[0.15em] uppercase py-2 border transition-colors ${
                                    discountMode === m.v
                                        ? "bg-amber-700/20 border-amber-600 text-amber-100"
                                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    {discountMode !== "none" && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    step={discountMode === "percent" ? "1" : "0.01"}
                                    min="0"
                                    max={discountMode === "percent" ? "100" : undefined}
                                    placeholder={discountMode === "percent" ? "e.g. 10" : "e.g. 5.00"}
                                    value={discountInput}
                                    onChange={(e) => setDiscountInput(e.target.value)}
                                    data-testid="sfb-discount-input"
                                    className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 text-white text-sm font-mono focus:border-amber-600 focus:outline-none"
                                />
                                <span className="text-[10px] text-zinc-500 tracking-[0.15em] uppercase w-12 text-right">
                                    {discountMode === "percent" ? "% off" : "USD off"}
                                </span>
                            </div>
                            {paymentMode === "recurring" && (
                                <div>
                                    <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Apply for</div>
                                    <div className="grid grid-cols-3 gap-1">
                                        {[
                                            { v: "once", label: "Once" },
                                            { v: "repeating", label: "N periods" },
                                            { v: "forever", label: "Forever" },
                                        ].map((d) => (
                                            <button
                                                key={d.v}
                                                type="button"
                                                data-testid={`sfb-discount-duration-${d.v}`}
                                                onClick={() => setDiscountDuration(d.v)}
                                                className={`text-[10px] tracking-[0.15em] uppercase py-1.5 border transition-colors ${
                                                    discountDuration === d.v
                                                        ? "bg-amber-700/20 border-amber-600 text-amber-100"
                                                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                                }`}
                                            >
                                                {d.label}
                                            </button>
                                        ))}
                                    </div>
                                    {discountDuration === "repeating" && (
                                        <input
                                            type="number"
                                            step="1"
                                            min="1"
                                            max={sub.commitment_count}
                                            placeholder={`Periods to apply (max ${sub.commitment_count})`}
                                            value={discountPeriods}
                                            onChange={(e) => setDiscountPeriods(e.target.value)}
                                            data-testid="sfb-discount-periods"
                                            className="mt-1.5 w-full px-3 py-2 bg-zinc-950 border border-zinc-800 text-white text-sm font-mono focus:border-amber-600 focus:outline-none"
                                        />
                                    )}
                                </div>
                            )}
                            {paymentMode === "upfront" && (
                                <div className="text-[10px] text-zinc-500 italic">
                                    Upfront is a single charge — discount applies once.
                                </div>
                            )}
                            <input
                                type="text"
                                maxLength={120}
                                placeholder="Reason (e.g., first-month comp, family discount)"
                                value={discountReason}
                                onChange={(e) => setDiscountReason(e.target.value)}
                                data-testid="sfb-discount-reason"
                                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs focus:border-amber-600 focus:outline-none"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* CTA */}
            <button
                type="button"
                disabled={busy || !selectedAthleteId || !sub}
                onClick={subscribe}
                data-testid="sfb-confirm"
                className="w-full px-4 py-2.5 bg-brand-red text-white text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                {paymentMode === "recurring" ? "Start recurring subscription" : "Charge upfront now"}
            </button>

            <div className="text-[10px] text-zinc-600 leading-relaxed">
                Edits in <span className="text-zinc-400">Owner → Pricing Blocks</span> reflect here instantly.
                {" "}Recurring path creates a real Stripe Subscription. Upfront path is a single PaymentIntent
                + a synthetic DB sub that grants access until commitment ends.
            </div>

            {/* Risky-charge confirmation modal — replaces window.confirm.
                Hard-gates $0 / forever discounts with a type-to-confirm token
                so a coach can't accidentally comp a paying customer. */}
            <RiskyChargeConfirmModal
                open={!!pending}
                onClose={() => !busy && setPending(null)}
                onConfirm={commit}
                title={pending?.title || ""}
                athleteName={pending?.athleteName}
                lines={pending?.lines || []}
                basePriceCents={pending?.basePriceCents}
                effectivePriceCents={pending?.effectivePriceCents}
                interval={pending?.interval}
                discount={pending?.discount}
                footNote={pending?.footNote}
                busy={busy}
            />
        </div>
    );
}
